/**
 * PromptDiffDialog — rich side-by-side or unified diff view for comparing
 * system prompt versions. Highlights added, removed, and unchanged lines.
 */
import { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, ToggleButtonGroup, ToggleButton, Chip,
} from '@mui/material';
import { CompareArrows, ViewStream, ViewColumn } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

interface Props {
  open: boolean;
  onClose: () => void;
  leftLabel: string;
  rightLabel: string;
  leftText: string;
  rightText: string;
}

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged';
  left: string;
  right: string;
  leftNum: number | null;
  rightNum: number | null;
}

function computeDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const result: DiffLine[] = [];

  // Simple LCS-based diff
  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldLines[i - 1] === newLines[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);

  // Backtrack to build diff
  let i = m, j = n;
  const stack: DiffLine[] = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      stack.push({ type: 'unchanged', left: oldLines[i - 1], right: newLines[j - 1], leftNum: i, rightNum: j });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'added', left: '', right: newLines[j - 1], leftNum: null, rightNum: j });
      j--;
    } else {
      stack.push({ type: 'removed', left: oldLines[i - 1], right: '', leftNum: i, rightNum: null });
      i--;
    }
  }
  stack.reverse().forEach((l) => result.push(l));
  return result;
}

export const PromptDiffDialog = ({ open, onClose, leftLabel, rightLabel, leftText, rightText }: Props) => {
  const theme = useTheme();
  const [mode, setMode] = useState<'side' | 'unified'>('side');
  const diff = useMemo(() => computeDiff(leftText, rightText), [leftText, rightText]);

  const stats = useMemo(() => {
    const added = diff.filter((d) => d.type === 'added').length;
    const removed = diff.filter((d) => d.type === 'removed').length;
    const unchanged = diff.filter((d) => d.type === 'unchanged').length;
    return { added, removed, unchanged };
  }, [diff]);

  const colors = {
    added: { bg: alpha('#4CAF50', 0.1), border: alpha('#4CAF50', 0.3), text: '#2E7D32' },
    removed: { bg: alpha('#F44336', 0.08), border: alpha('#F44336', 0.25), text: '#C62828' },
    unchanged: { bg: 'transparent', border: 'transparent', text: theme.palette.text.primary },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: '85vh', borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CompareArrows sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
            Compare Prompts
          </Typography>
          <Chip label={`+${stats.added}`} size="small" sx={{ backgroundColor: colors.added.bg, color: colors.added.text, fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
          <Chip label={`-${stats.removed}`} size="small" sx={{ backgroundColor: colors.removed.bg, color: colors.removed.text, fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
          <Chip label={`${stats.unchanged} unchanged`} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
        </Box>
        <ToggleButtonGroup size="small" value={mode} exclusive onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="side"><ViewColumn sx={{ fontSize: 16, mr: 0.5 }} /> Side-by-side</ToggleButton>
          <ToggleButton value="unified"><ViewStream sx={{ fontSize: 16, mr: 0.5 }} /> Unified</ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Labels */}
        {mode === 'side' ? (
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ flex: 1, px: 2, py: 0.75, backgroundColor: colors.removed.bg, borderRight: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{leftLabel}</Typography>
            </Box>
            <Box sx={{ flex: 1, px: 2, py: 0.75, backgroundColor: colors.added.bg }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{rightLabel}</Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: '#F8F9FA' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              {leftLabel} → {rightLabel}
            </Typography>
          </Box>
        )}

        {/* Diff content */}
        <Box sx={{ flex: 1, overflow: 'auto', fontFamily: "'Fira Code', 'Consolas', monospace", fontSize: '0.78rem', lineHeight: 1.7 }}>
          {mode === 'side' ? (
            <SideBySideView diff={diff} colors={colors} theme={theme} />
          ) : (
            <UnifiedView diff={diff} colors={colors} />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

function SideBySideView({ diff, colors, theme }: { diff: DiffLine[]; colors: any; theme: any }) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100%' }}>
      {/* Left */}
      <Box sx={{ flex: 1, borderRight: `1px solid ${theme.palette.divider}` }}>
        {diff.map((line, i) => (
          <Box key={`l-${i}`} sx={{
            display: 'flex', px: 1, minHeight: 24,
            backgroundColor: line.type === 'removed' ? colors.removed.bg : line.type === 'added' ? alpha('#000', 0.02) : 'transparent',
            borderLeft: line.type === 'removed' ? `3px solid ${colors.removed.text}` : '3px solid transparent',
          }}>
            <Typography component="span" sx={{ width: 36, textAlign: 'right', pr: 1, color: 'text.disabled', fontSize: '0.7rem', userSelect: 'none', flexShrink: 0 }}>
              {line.leftNum || ''}
            </Typography>
            <Typography component="span" sx={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1,
              color: line.type === 'removed' ? colors.removed.text : line.type === 'added' ? 'text.disabled' : 'text.primary',
              textDecoration: line.type === 'removed' ? 'line-through' : 'none',
              fontStyle: line.type === 'added' ? 'italic' : 'normal',
            }}>
              {line.type === 'added' ? '' : line.left}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* Right */}
      <Box sx={{ flex: 1 }}>
        {diff.map((line, i) => (
          <Box key={`r-${i}`} sx={{
            display: 'flex', px: 1, minHeight: 24,
            backgroundColor: line.type === 'added' ? colors.added.bg : line.type === 'removed' ? alpha('#000', 0.02) : 'transparent',
            borderLeft: line.type === 'added' ? `3px solid ${colors.added.text}` : '3px solid transparent',
          }}>
            <Typography component="span" sx={{ width: 36, textAlign: 'right', pr: 1, color: 'text.disabled', fontSize: '0.7rem', userSelect: 'none', flexShrink: 0 }}>
              {line.rightNum || ''}
            </Typography>
            <Typography component="span" sx={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1,
              color: line.type === 'added' ? colors.added.text : line.type === 'removed' ? 'text.disabled' : 'text.primary',
              fontWeight: line.type === 'added' ? 500 : 400,
            }}>
              {line.type === 'removed' ? '' : line.right}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function UnifiedView({ diff, colors }: { diff: DiffLine[]; colors: any }) {
  return (
    <Box>
      {diff.map((line, i) => (
        <Box key={i} sx={{
          display: 'flex', px: 1, minHeight: 24,
          backgroundColor: line.type === 'added' ? colors.added.bg : line.type === 'removed' ? colors.removed.bg : 'transparent',
          borderLeft: line.type !== 'unchanged' ? `3px solid ${line.type === 'added' ? colors.added.text : colors.removed.text}` : '3px solid transparent',
        }}>
          <Typography component="span" sx={{
            width: 20, textAlign: 'center', color: line.type === 'added' ? colors.added.text : line.type === 'removed' ? colors.removed.text : 'text.disabled',
            fontSize: '0.75rem', fontWeight: 600, userSelect: 'none', flexShrink: 0,
          }}>
            {line.type === 'added' ? '+' : line.type === 'removed' ? '−' : ''}
          </Typography>
          <Typography component="span" sx={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1,
            color: line.type === 'added' ? colors.added.text : line.type === 'removed' ? colors.removed.text : 'text.primary',
            textDecoration: line.type === 'removed' ? 'line-through' : 'none',
            fontWeight: line.type !== 'unchanged' ? 500 : 400,
          }}>
            {line.type === 'removed' ? line.left : line.right || line.left}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
